import React, { useState, useEffect } from 'react';
import { FiEdit, FiRefreshCw, FiEye, FiHome, FiMapPin, FiCamera, FiMap, FiNavigation, FiShield, FiUsers } from 'react-icons/fi';
import { MdRestaurant, MdLocalDining, MdVisibility, MdDirectionsWalk, MdAccountBox, MdBusiness, MdEmail, MdCampaign, MdFamilyRestroom, MdConfirmationNumber, MdDirectionsRun } from 'react-icons/md';
import { HiOutlineNewspaper, HiOutlineChatBubbleLeft, HiOutlineGift } from 'react-icons/hi2';
import { BiSolidOffer } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import { articleService } from '../services/articleService';
import { toast } from 'react-hot-toast';
import { countryOptions, getCountryCode, getCountryName } from '../utils/countries';

const ArticlesScreen = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [showModal, setShowModal] = useState(false);
    
    // Filtros y paginación
    const [filters, setFilters] = useState({
        search: '',
        pais: '',
        ciudad: '',
        tipo: ''
    });
    const [tempFilters, setTempFilters] = useState({
        search: '',
        pais: '',
        ciudad: '',
        tipo: ''
    });
    
    // Paginación
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        pages: 0
    });
    
    // Opciones de límite de paginación
    const limitOptions = [20, 50, 100, 200];
    
    // Ordenamiento
    const [sorting, setSorting] = useState({
        field: 'totalVentas',
        order: 'DESC'
    });
    
    // Estado para el formulario de edición
    const [formData, setFormData] = useState({
        nombre: '',
        ciudad: '',
        pais: '',
        activo: true,
        tipo: ''
    });

    // Estado para los tipos disponibles
    const [articleTypes, setArticleTypes] = useState([]);

    // Estados para merge
    const [selectedArticles, setSelectedArticles] = useState([]);
    const [mergeMode, setMergeMode] = useState(false);
    const [targetArticleId, setTargetArticleId] = useState(null);
    const [mergeLoading, setMergeLoading] = useState(false);
    const [showMergeConfirmModal, setShowMergeConfirmModal] = useState(false);
    const [mergeConfirmData, setMergeConfirmData] = useState(null);

    // Estados para la edición masiva
    const [selectedArticlesForEdit, setSelectedArticlesForEdit] = useState([]);
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [bulkEditFormData, setBulkEditFormData] = useState({
        pais: '',
        ciudad: '',
        tipo: ''
    });
    const [bulkEditLoading, setBulkEditLoading] = useState(false);
    const [bulkEditResult, setBulkEditResult] = useState(null);

    // Estados para la eliminación masiva (usando la misma selección)
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
    const [orphanedArticles, setOrphanedArticles] = useState([]);
    const [showOrphanedModal, setShowOrphanedModal] = useState(false);
    const [orphanedLoading, setOrphanedLoading] = useState(false);

    const loadArticles = async () => {
        try {
            setLoading(true);
            
            // Preparar filtros para la API, incluyendo paginación y ordenamiento
            const apiFilters = {
                ...filters,
                page: pagination.page,
                limit: pagination.limit,
                sortBy: sorting.field,
                sortOrder: sorting.order
            };
            
            console.log('Enviando filtros a la API:', apiFilters);
            
            const data = await articleService.getArticles(apiFilters);
            
            if (data.articles) {
                // Si la API devuelve un objeto con paginación
                setArticles(data.articles);
                setPagination({
                    page: data.currentPage || 1,
                    limit: pagination.limit,
                    total: data.totalCount || 0,
                    pages: data.totalPages || 0
                });
            } else {
                // Si la API devuelve un array simple
                setArticles(data);
                setPagination(prev => ({
                    ...prev,
                    total: data.length,
                    pages: Math.ceil(data.length / prev.limit)
                }));
            }
        } catch (error) {
            console.error('Error al cargar artículos:', error);
            toast.error('Error al cargar los artículos: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const loadArticleTypes = async () => {
        try {
            const data = await articleService.getArticleTypes();
            setArticleTypes(data.types || []);
        } catch (error) {
            console.error('Error al cargar tipos de artículos:', error);
            // No mostrar error al usuario, los tipos son opcionales
        }
    };

    useEffect(() => {
        loadArticles();
    }, [filters, pagination.page, pagination.limit, sorting]);

    useEffect(() => {
        loadArticleTypes();
    }, []);

    const handleEditClick = (article) => {
        setSelectedArticle(article);
        setFormData({
            nombre: article.nombre || '',
            ciudad: article.ciudad || '',
            pais: getCountryCode(article.pais) || '',
            activo: article.activo !== undefined ? article.activo : true,
            tipo: article.tipo || ''
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedArticle(null);
        setFormData({ nombre: '', ciudad: '', pais: '', activo: true, tipo: '' });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleTempFilterChange = (e) => {
        const { name, value } = e.target;
        setTempFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyFilters = () => {
        setFilters(tempFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const clearFilters = () => {
        const clearedFilters = {
            search: '',
            pais: '',
            ciudad: '',
            tipo: ''
        };
        setTempFilters(clearedFilters);
        setFilters(clearedFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.pages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setPagination(prev => ({
            ...prev,
            limit: newLimit,
            page: 1
        }));
    };

    const handleSort = (field) => {
        setSorting(prev => ({
            field,
            order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC'
        }));
    };

    const renderSortIcon = (field) => {
        if (sorting.field !== field) return null;
        return sorting.order === 'ASC' ? ' ↑' : ' ↓';
    };

    // Función para obtener el icono según el tipo de artículo
    const getTipoIcon = (tipo) => {
        if (!tipo) return null;
        
        // Iconos para Posts
        if (tipo.startsWith('[Post]')) {
            const postIcons = {
                '[Post] - Dónde alojarse': <FiHome className="w-3 h-3" />,
                '[Post] - Dónde comer': <MdRestaurant className="w-3 h-3" />,
                '[Post] - Comida típica': <MdLocalDining className="w-3 h-3" />,
                '[Post] - Qué ver en': <MdVisibility className="w-3 h-3" />,
                '[Post] - Rutas': <MdDirectionsWalk className="w-3 h-3" />,
                '[Post] - Viajar a': <FiNavigation className="w-3 h-3" />,
                '[Post] - Monumentos': <FiMapPin className="w-3 h-3" />,
                '[Post] - Cómo ir': <FiMap className="w-3 h-3" />,
                '[Post] - Excursiones': <FiCamera className="w-3 h-3" />,
                '[Post] - Opinión': <HiOutlineNewspaper className="w-3 h-3" />,
                '[Post] - Seguros': <FiShield className="w-3 h-3" />,
                '[Post] - Niños': <MdFamilyRestroom className="w-3 h-3" />,
                '[Post] - Visitar': <FiUsers className="w-3 h-3" />,
                '[Post] - Entradas': <MdConfirmationNumber className="w-3 h-3" />,
                '[Post] - Guía de viaje': <MdDirectionsRun className="w-3 h-3" />
            };
            return postIcons[tipo] || <HiOutlineNewspaper className="w-3 h-3" />;
        }
        
        // Iconos para RRSS
        if (tipo.startsWith('[RRSS]')) {
            const rrssIcons = {
                '[RRSS] - Stories': <FiCamera className="w-3 h-3" />,
                '[RRSS] - Manychat': <HiOutlineChatBubbleLeft className="w-3 h-3" />,
                '[RRSS] - Bio': <MdAccountBox className="w-3 h-3" />
            };
            return rrssIcons[tipo] || <FiCamera className="w-3 h-3" />;
        }
        
        // Iconos para Email
        if (tipo.startsWith('[Email]')) {
            const emailIcons = {
                '[Email] - Funnel': <MdCampaign className="w-3 h-3" />,
                '[Email] - Newsletter': <MdEmail className="w-3 h-3" />,
                '[Email] - Leadmagnet': <HiOutlineGift className="w-3 h-3" />,
                '[Email] - 1a1': <HiOutlineChatBubbleLeft className="w-3 h-3" />
            };
            return emailIcons[tipo] || <MdEmail className="w-3 h-3" />;
        }
        
        // Iconos para Clientes
        if (tipo.startsWith('[Clientes]')) {
            const clientIcons = {
                '[Clientes] - Producto propio': <BiSolidOffer className="w-3 h-3" />,
                '[Clientes] - Asesoria': <MdBusiness className="w-3 h-3" />
            };
            return clientIcons[tipo] || <MdBusiness className="w-3 h-3" />;
        }
        
        return null;
    };

    // Función para obtener el color del badge según el tipo de artículo
    const getTipoBadgeColor = (tipo) => {
        if (!tipo) return 'badge-ghost';
        
        // Colores por categoría
        if (tipo.startsWith('[Post]')) {
            const postTypes = {
                '[Post] - Dónde alojarse': 'badge-primary',
                '[Post] - Dónde comer': 'badge-secondary', 
                '[Post] - Comida típica': 'badge-accent',
                '[Post] - Qué ver en': 'badge-info',
                '[Post] - Rutas': 'badge-success',
                '[Post] - Viajar a': 'badge-warning',
                '[Post] - Monumentos': 'badge-error',
                '[Post] - Cómo ir': 'badge-primary badge-outline',
                '[Post] - Excursiones': 'badge-secondary badge-outline',
                '[Post] - Opinión': 'badge-accent badge-outline',
                '[Post] - Seguros': 'badge-info badge-outline',
                '[Post] - Niños': 'badge-success badge-outline',
                '[Post] - Visitar': 'badge-warning badge-outline',
                '[Post] - Entradas': 'badge-error badge-outline',
                '[Post] - Guía de viaje': 'badge-primary badge-outline'
            };
            return postTypes[tipo] || 'badge-neutral';
        }
        
        if (tipo.startsWith('[RRSS]')) {
            const rrssTypes = {
                '[RRSS] - Stories': 'bg-pink-500 text-pink-100 border-pink-500',
                '[RRSS] - Manychat': 'bg-purple-500 text-purple-100 border-purple-500', 
                '[RRSS] - Bio': 'bg-indigo-500 text-indigo-100 border-indigo-500'
            };
            return rrssTypes[tipo] || 'bg-pink-400 text-pink-100 border-pink-400';
        }
        
        if (tipo.startsWith('[Email]')) {
            const emailTypes = {
                '[Email] - Funnel': 'bg-orange-500 text-orange-100 border-orange-500',
                '[Email] - Newsletter': 'bg-yellow-400 text-yellow-900 border-yellow-400',
                '[Email] - Leadmagnet': 'bg-amber-400 text-amber-900 border-amber-400',
                '[Email] - 1a1': 'bg-orange-400 text-orange-100 border-orange-400'
            };
            return emailTypes[tipo] || 'bg-orange-400 text-orange-100 border-orange-400';
        }
        
        if (tipo.startsWith('[Clientes]')) {
            const clientTypes = {
                '[Clientes] - Producto propio': 'bg-red-500 text-red-100 border-red-500',
                '[Clientes] - Asesoria': 'bg-gray-500 text-gray-100 border-gray-500'
            };
            return clientTypes[tipo] || 'bg-red-400 text-red-100 border-red-400';
        }
        
        return 'badge-ghost';
    };

    // Función para obtener texto corto del tipo
    const getTipoShortText = (tipo) => {
        if (!tipo) return '';
        
        // Versiones cortas para Posts
        if (tipo.startsWith('[Post]')) {
            const shortTexts = {
                '[Post] - Dónde alojarse': 'Alojarse',
                '[Post] - Dónde comer': 'Comer',
                '[Post] - Comida típica': 'Comida',
                '[Post] - Qué ver en': 'Qué ver',
                '[Post] - Rutas': 'Rutas',
                '[Post] - Viajar a': 'Viajar',
                '[Post] - Monumentos': 'Monumentos',
                '[Post] - Cómo ir': 'Cómo ir',
                '[Post] - Excursiones': 'Excursiones',
                '[Post] - Opinión': 'Opinión',
                '[Post] - Seguros': 'Seguros',
                '[Post] - Niños': 'Niños',
                '[Post] - Visitar': 'Visitar',
                '[Post] - Entradas': 'Entradas',
                '[Post] - Guía de viaje': 'Guía de viaje'
            };
            return shortTexts[tipo] || tipo.replace('[Post] - ', '');
        }
        
        // Versiones cortas para RRSS
        if (tipo.startsWith('[RRSS]')) {
            const shortTexts = {
                '[RRSS] - Stories': 'Stories',
                '[RRSS] - Manychat': 'Manychat',
                '[RRSS] - Bio': 'Bio'
            };
            return shortTexts[tipo] || tipo.replace('[RRSS] - ', '');
        }
        
        // Versiones cortas para Email
        if (tipo.startsWith('[Email]')) {
            const shortTexts = {
                '[Email] - Funnel': 'Funnel',
                '[Email] - Newsletter': 'Newsletter',
                '[Email] - Leadmagnet': 'Leadmagnet',
                '[Email] - 1a1': '1a1'
            };
            return shortTexts[tipo] || tipo.replace('[Email] - ', '');
        }
        
        // Versiones cortas para Clientes
        if (tipo.startsWith('[Clientes]')) {
            const shortTexts = {
                '[Clientes] - Producto propio': 'Producto',
                '[Clientes] - Asesoria': 'Asesoria'
            };
            return shortTexts[tipo] || tipo.replace('[Clientes] - ', '');
        }
        
        return tipo;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await articleService.updateArticle(selectedArticle.id, formData);
            toast.success('Artículo actualizado correctamente');
            handleCloseModal();
            await loadArticles();
        } catch (error) {
            console.error('Error al actualizar artículo:', error);
            toast.error('Error al actualizar el artículo: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleRecalculateStats = async (articleId) => {
        try {
            toast.loading('Recalculando estadísticas...', { id: 'recalculating' });
            await articleService.recalculateArticleStats(articleId);
            toast.success('Estadísticas recalculadas correctamente', { id: 'recalculating' });
            await loadArticles();
        } catch (error) {
            console.error('Error al recalcular estadísticas:', error);
            toast.error('Error al recalcular estadísticas: ' + (error.response?.data?.message || error.message), { id: 'recalculating' });
        }
    };

    // Obtener opciones únicas para filtros
    const uniqueCountries = Array.from(new Set(articles.map(a => a.pais).filter(Boolean))).sort();
    const uniqueCities = Array.from(new Set(articles.map(a => a.ciudad).filter(Boolean))).sort();

    // Función para manejar selección de artículos
    const handleArticleSelection = (articleId) => {
        setSelectedArticles(prev => {
            if (prev.includes(articleId)) {
                return prev.filter(id => id !== articleId);
            } else {
                return [...prev, articleId];
            }
        });
    };

    // Función para seleccionar todos los artículos
    const handleSelectAll = () => {
        if (selectedArticles.length === articles.length) {
            setSelectedArticles([]);
        } else {
            setSelectedArticles(articles.map(article => article.id));
        }
    };

    // Función para iniciar modo merge
    const startMergeMode = () => {
        setMergeMode(true);
        setSelectedArticles([]);
        setTargetArticleId(null);
    };

    // Función para cancelar merge
    const cancelMerge = () => {
        setMergeMode(false);
        setSelectedArticles([]);
        setTargetArticleId(null);
    };

    // Función para ejecutar merge
    const executeMerge = async () => {
        if (!targetArticleId || selectedArticles.length === 0) {
            alert('Selecciona un artículo destino y al menos un artículo origen');
            return;
        }

        const sourceArticleIds = selectedArticles.filter(id => id !== targetArticleId);
        
        if (sourceArticleIds.length === 0) {
            alert('Debe haber al menos un artículo origen diferente al destino');
            return;
        }

        const targetArticle = articles.find(a => a.id === targetArticleId);
        const sourceArticles = articles.filter(a => sourceArticleIds.includes(a.id));
        
        setMergeConfirmData({
            targetArticle,
            sourceArticles
        });
        setShowMergeConfirmModal(true);
    };

    const handleConfirmMerge = async () => {
        try {
            setMergeLoading(true);
            
            const sourceArticleIds = selectedArticles.filter(id => id !== targetArticleId);
            const result = await articleService.mergeArticles(targetArticleId, sourceArticleIds);
            
            toast.success(`Merge completado con éxito!\n\n${result.mergedCount} artículos fusionados\n${result.salesMoved} ventas consolidadas`);
            
            // Recargar la lista
            loadArticles();
            cancelMerge();
            setShowMergeConfirmModal(false);
            
        } catch (error) {
            console.error('Error en merge:', error);
            toast.error('Error al hacer merge: ' + error.message);
        } finally {
            setMergeLoading(false);
        }
    };

    // Funciones para la edición masiva
    const handleArticleSelectionForEdit = (articleId) => {
        setSelectedArticlesForEdit(prev => {
            if (prev.includes(articleId)) {
                return prev.filter(id => id !== articleId);
            } else {
                return [...prev, articleId];
            }
        });
    };

    const handleSelectAllArticlesForEdit = () => {
        if (selectedArticlesForEdit.length === articles.length) {
            setSelectedArticlesForEdit([]);
        } else {
            setSelectedArticlesForEdit(articles.map(article => article.id));
        }
    };

    const handleShowBulkEditModal = () => {
        if (selectedArticlesForEdit.length === 0) {
            toast.error('Debes seleccionar al menos un artículo para editar');
            return;
        }
        
        setBulkEditFormData({
            pais: '',
            ciudad: '',
            tipo: ''
        });
        setBulkEditResult(null);
        setShowBulkEditModal(true);
    };

    const handleCloseBulkEditModal = () => {
        setShowBulkEditModal(false);
        setBulkEditResult(null);
    };

    const handleBulkEditInputChange = (e) => {
        const { name, value } = e.target;
        setBulkEditFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'pais' ? { ciudad: '' } : {})
        }));
    };

    const handleBulkEditSubmit = async (e) => {
        e.preventDefault();
        
        if (!bulkEditFormData.pais && !bulkEditFormData.ciudad && !bulkEditFormData.tipo) {
            toast.error('Debes proporcionar al menos país, ciudad o tipo para actualizar');
            return;
        }
        
        try {
            setBulkEditLoading(true);
            
            const updateData = {};
            if (bulkEditFormData.pais) updateData.pais = bulkEditFormData.pais;
            if (bulkEditFormData.ciudad) updateData.ciudad = bulkEditFormData.ciudad;
            if (bulkEditFormData.tipo) updateData.tipo = bulkEditFormData.tipo;
            
            const result = await articleService.updateArticlesBulk(selectedArticlesForEdit, updateData);
            
            setBulkEditResult(result);
            toast.success(`Se actualizaron ${result.updatedCount} artículos correctamente`);
            
            await loadArticles();
            setSelectedArticlesForEdit([]);
        } catch (error) {
            console.error('Error al actualizar artículos:', error);
            toast.error('Error al actualizar artículos: ' + (error.response?.data?.message || error.message));
        } finally {
            setBulkEditLoading(false);
        }
    };

    // Funciones para eliminación en bulk (usando la misma selección)
    const handleShowBulkDeleteModal = () => {
        if (selectedArticlesForEdit.length === 0) {
            toast.error('Debes seleccionar al menos un artículo para eliminar');
            return;
        }
        setShowBulkDeleteModal(true);
    };

    const handleCloseBulkDeleteModal = () => {
        setShowBulkDeleteModal(false);
    };

    const handleBulkDeleteSubmit = async () => {
        try {
            setBulkDeleteLoading(true);
            
            const result = await articleService.deleteArticlesBulk(selectedArticlesForEdit);
            
            toast.success(`Se eliminaron ${result.deletedCount} artículos correctamente`);
            
            await loadArticles();
            setSelectedArticlesForEdit([]);
            setShowBulkDeleteModal(false);
        } catch (error) {
            console.error('Error al eliminar artículos:', error);
            toast.error('Error al eliminar artículos: ' + (error.response?.data?.message || error.message));
        } finally {
            setBulkDeleteLoading(false);
        }
    };

    // Funciones para artículos huérfanos
    const findOrphanedArticles = async () => {
        try {
            setOrphanedLoading(true);
            const result = await articleService.findOrphanedArticles();
            setOrphanedArticles(result.articles || []);
            setShowOrphanedModal(true);
        } catch (error) {
            console.error('Error buscando artículos huérfanos:', error);
            toast.error('Error buscando artículos huérfanos: ' + (error.response?.data?.message || error.message));
        } finally {
            setOrphanedLoading(false);
        }
    };

    const handleDeleteOrphanedArticles = async () => {
        if (orphanedArticles.length === 0) return;
        
        const confirmMessage = `⚠️ ¿Estás seguro de que quieres eliminar ${orphanedArticles.length} artículos huérfanos?\n\nEsta acción NO se puede deshacer.`;
        
        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            setOrphanedLoading(true);
            
            const orphanedIds = orphanedArticles.map(article => article.id);
            const result = await articleService.deleteArticlesBulk(orphanedIds);
            
            toast.success(`Se eliminaron ${result.deletedCount} artículos huérfanos correctamente`);
            
            await loadArticles();
            setOrphanedArticles([]);
            setShowOrphanedModal(false);
        } catch (error) {
            console.error('Error eliminando artículos huérfanos:', error);
            toast.error('Error eliminando artículos huérfanos: ' + (error.response?.data?.message || error.message));
        } finally {
            setOrphanedLoading(false);
        }
    };

    // Función para recrear todos los artículos
    const recreateAllArticles = async () => {
        const confirmMessage = `⚠️ ATENCIÓN: Esta acción eliminará todos los artículos existentes y los recreará desde las ventas.\n\n¿Estás seguro de que quieres continuar?\n\nEsta acción NO se puede deshacer.`;
        
        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            setLoading(true);
            
            const result = await articleService.recreateAllArticles();
            
            alert(`Recreación completada con éxito!\n\n✅ Artículos eliminados: ${result.deletedCount}\n📝 Artículos creados: ${result.created}\n⚠️ Artículos omitidos: ${result.skipped}\n🔄 Artículos recalculados: ${result.recalculated}\n📊 Total final: ${result.totalArticles}`);
            
            // Recargar la lista
            await loadArticles();
            
        } catch (error) {
            console.error('Error recreando artículos:', error);
            alert('Error al recrear artículos: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading && articles.length === 0) {
        return (
            <div className="flex justify-center p-8">
                <span className="loading loading-spinner loading-lg"></span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="page-header">
                <h1 className="page-title">Gestión de Artículos</h1>
                <div className="page-actions">
                    {/* Botón de refrescar */}
                    <button 
                        onClick={loadArticles} 
                        className="btn btn-outline btn-sm"
                        title="Refrescar artículos"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refrescar
                    </button>

                    {/* Botones de Merge */}
                    {!mergeMode ? (
                        <button 
                            className="btn btn-secondary btn-sm"
                            onClick={startMergeMode}
                            title="Fusionar artículos duplicados"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                            Hacer Merge
                        </button>
                    ) : (
                        <>
                            <div className="badge badge-info badge-sm mr-1">
                                {selectedArticles.length} seleccionados
                            </div>
                            <button 
                                className="btn btn-success btn-sm"
                                onClick={executeMerge}
                                disabled={!targetArticleId || selectedArticles.length < 2 || mergeLoading}
                                title="Ejecutar fusión de artículos"
                            >
                                {mergeLoading ? (
                                    <span className="loading loading-spinner loading-xs"></span>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Ejecutar
                                    </>
                                )}
                            </button>
                            <button 
                                className="btn btn-ghost btn-sm"
                                onClick={cancelMerge}
                                title="Cancelar modo merge"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Cancelar
                            </button>
                        </>
                    )}

                    {/* Botón de edición masiva */}
                    <button 
                        onClick={handleShowBulkEditModal} 
                        className="btn btn-info btn-sm"
                        title="Editar país/ciudad de múltiples artículos"
                        disabled={selectedArticlesForEdit.length === 0}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Editar ({selectedArticlesForEdit.length})
                    </button>

                    {/* Botón de eliminación masiva */}
                    <button 
                        onClick={handleShowBulkDeleteModal} 
                        className="btn btn-error btn-sm"
                        title="Eliminar múltiples artículos seleccionados"
                        disabled={selectedArticlesForEdit.length === 0}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Eliminar ({selectedArticlesForEdit.length})
                    </button>

                    {/* Botón de búsqueda de huérfanos */}
                    <button 
                        onClick={findOrphanedArticles} 
                        className="btn btn-ghost btn-sm"
                        disabled={orphanedLoading}
                        title="Buscar artículos sin ventas"
                    >
                        {orphanedLoading ? (
                            <span className="loading loading-spinner loading-xs"></span>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                Huérfanos
                            </>
                        )}
                    </button>

                    {/* Botón de recrear todos */}
                    <button 
                        onClick={recreateAllArticles} 
                        className="btn btn-warning btn-sm"
                        disabled={loading}
                        title="Recrear todos los artículos desde las ventas"
                    >
                        {loading ? (
                            <span className="loading loading-spinner loading-xs"></span>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Recrear
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="card bg-base-100 shadow-xl mb-6">
                <div className="card-body">
                    <h2 className="card-title mb-4">Filtros</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Buscar</span>
                            </label>
                            <input
                                type="text"
                                name="search"
                                value={tempFilters.search}
                                onChange={handleTempFilterChange}
                                placeholder="Buscar por nombre o identificador..."
                                className="input input-bordered w-full"
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">País</span>
                            </label>
                            <select
                                name="pais"
                                value={tempFilters.pais}
                                onChange={handleTempFilterChange}
                                className="select select-bordered w-full"
                            >
                                <option value="">Todos los países</option>
                                <option value="__SIN_PAIS__">Sin país</option>
                                {uniqueCountries.map(country => (
                                    <option key={country} value={country}>
                                        {getCountryName(country)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Ciudad</span>
                            </label>
                            <select
                                name="ciudad"
                                value={tempFilters.ciudad}
                                onChange={handleTempFilterChange}
                                className="select select-bordered w-full"
                            >
                                <option value="">Todas las ciudades</option>
                                {uniqueCities.map(city => (
                                    <option key={city} value={city}>
                                        {city}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Tipo</span>
                            </label>
                            <select
                                name="tipo"
                                value={tempFilters.tipo}
                                onChange={handleTempFilterChange}
                                className="select select-bordered w-full"
                            >
                                <option value="">Todos los tipos</option>
                                <option value="__SIN_TIPO__">Sin tipo</option>
                                {articleTypes.map((tipo, index) => (
                                    <option key={index} value={tipo}>
                                        {tipo}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-end mt-4 gap-2">
                        <button 
                            className="btn btn-ghost"
                            onClick={clearFilters}
                        >
                            Limpiar Filtros
                        </button>
                        <button 
                            className="btn btn-primary"
                            onClick={applyFilters}
                        >
                            Aplicar Filtros
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-base-100 shadow-xl rounded-box">
                {articles.length === 0 ? (
                    <div className="p-8 text-center">
                        <p className="text-base-content/70">No hay artículos disponibles</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table table-zebra w-full">
                            <thead>
                                <tr>
                                    {mergeMode && (
                                        <th className="w-16">
                                            <label>
                                                <input 
                                                    type="checkbox" 
                                                    className="checkbox"
                                                    checked={selectedArticles.length === articles.length && articles.length > 0}
                                                    onChange={handleSelectAll}
                                                />
                                            </label>
                                        </th>
                                    )}
                                    {!mergeMode && (
                                        <>
                                            <th className="w-16">
                                                <label title="Seleccionar artículos">
                                                    <input 
                                                        type="checkbox" 
                                                        className="checkbox checkbox-primary"
                                                        checked={selectedArticlesForEdit.length === articles.length && articles.length > 0}
                                                        onChange={handleSelectAllArticlesForEdit}
                                                    />
                                                </label>
                                            </th>
                                        </>
                                    )}
                                    {mergeMode && (
                                        <th className="w-20">Destino</th>
                                    )}
                                    <th className="cursor-pointer" onClick={() => handleSort('id')}>
                                        ID{renderSortIcon('id')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('identificador')}>
                                        Identificador{renderSortIcon('identificador')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('nombre')}>
                                        Nombre{renderSortIcon('nombre')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('pais')}>
                                        País{renderSortIcon('pais')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('ciudad')}>
                                        Ciudad{renderSortIcon('ciudad')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('tipo')}>
                                        Tipo{renderSortIcon('tipo')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('totalVentas')}>
                                        Ventas{renderSortIcon('totalVentas')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('totalComision')}>
                                        Comisión{renderSortIcon('totalComision')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('ultimaVenta')}>
                                        Última Venta{renderSortIcon('ultimaVenta')}
                                    </th>
                                    <th className="cursor-pointer" onClick={() => handleSort('activo')}>
                                        Estado{renderSortIcon('activo')}
                                    </th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {articles.map(article => (
                                    <tr key={article.id} className={
                                        mergeMode 
                                            ? (selectedArticles.includes(article.id) ? 'bg-blue-50' : '')
                                            : (selectedArticlesForEdit.includes(article.id) ? 'bg-blue-50' : '')
                                    }>
                                        {mergeMode && (
                                            <td>
                                                <label>
                                                    <input 
                                                        type="checkbox" 
                                                        className="checkbox"
                                                        checked={selectedArticles.includes(article.id)}
                                                        onChange={() => handleArticleSelection(article.id)}
                                                    />
                                                </label>
                                            </td>
                                        )}
                                        {!mergeMode && (
                                            <>
                                                <td>
                                                    <label>
                                                        <input 
                                                            type="checkbox" 
                                                            className="checkbox checkbox-primary"
                                                            checked={selectedArticlesForEdit.includes(article.id)}
                                                            onChange={() => handleArticleSelectionForEdit(article.id)}
                                                        />
                                                    </label>
                                                </td>
                                            </>
                                        )}
                                        {mergeMode && (
                                            <td>
                                                <label>
                                                    <input 
                                                        type="radio" 
                                                        name="targetArticle"
                                                        className="radio radio-primary"
                                                        checked={targetArticleId === article.id}
                                                        onChange={() => setTargetArticleId(article.id)}
                                                        disabled={!selectedArticles.includes(article.id)}
                                                    />
                                                </label>
                                            </td>
                                        )}
                                        <td>
                                            <Link 
                                                to={`/sales?article=${article.identificador}`}
                                                className="link link-primary font-medium"
                                                title="Ver ventas de este artículo"
                                            >
                                                {article.id}
                                            </Link>
                                        </td>
                                        <td className="font-mono text-sm">
                                            {article.identificador}
                                        </td>
                                        <td>
                                            <div className="font-medium">
                                                {article.nombre}
                                            </div>
                                        </td>
                                        <td>
                                            {article.pais ? (
                                                <span title={article.pais}>
                                                    {getCountryName(article.pais)}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td>{article.ciudad || '-'}</td>
                                        <td>
                                            {article.tipo ? (
                                                <span 
                                                    className={`badge ${getTipoBadgeColor(article.tipo)} flex items-center gap-1 px-2 py-1`}
                                                    title={article.tipo}
                                                >
                                                    {getTipoIcon(article.tipo)}
                                                    <span className="text-xs font-medium">{getTipoShortText(article.tipo)}</span>
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="text-right">
                                            <span className="font-bold">
                                                {article.totalVentas}
                                            </span>
                                        </td>
                                        <td className="text-right">
                                            <span className="font-bold text-green-600">
                                                {parseFloat(article.totalComision || 0).toFixed(2)} €
                                            </span>
                                        </td>
                                        <td>
                                            {article.ultimaVenta ? 
                                                new Date(article.ultimaVenta).toLocaleDateString('es-ES') 
                                                : '-'
                                            }
                                        </td>
                                        <td>
                                            <div className={`badge ${article.activo ? 'badge-success' : 'badge-error'}`}>
                                                {article.activo ? 'Activo' : 'Inactivo'}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <Link 
                                                    to={`/articles/${article.id}`}
                                                    className="table-action-btn table-action-btn-info"
                                                    title="Ver detalles del artículo"
                                                >
                                                    <FiEye size={18} />
                                                </Link>
                                                <button 
                                                    onClick={() => handleEditClick(article)}
                                                    className="table-action-btn table-action-btn-neutral"
                                                    title="Editar artículo"
                                                >
                                                    <FiEdit size={18} />
                                                </button>
                                                <button 
                                                    onClick={() => handleRecalculateStats(article.id)}
                                                    className="table-action-btn table-action-btn-success"
                                                    title="Recalcular estadísticas"
                                                >
                                                    <FiRefreshCw size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
                
            {/* Paginación */}
            {articles.length > 0 && (
                <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center gap-4">
                        <div className="text-sm">
                            Mostrando <span className="font-medium">{articles.length}</span> de <span className="font-medium">{pagination.total}</span> resultados
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Mostrar:</span>
                            <select 
                                className="select select-bordered select-sm"
                                value={pagination.limit}
                                onChange={handleLimitChange}
                            >
                                {limitOptions.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="join">
                        <button 
                            className="join-item btn btn-sm" 
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.page === 1}
                        >
                            «
                        </button>
                        <button 
                            className="join-item btn btn-sm" 
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={pagination.page === 1}
                        >
                            ‹
                        </button>
                        
                        {/* Renderizar números de página */}
                        {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                            let pageNum;
                            if (pagination.pages <= 5) {
                                pageNum = i + 1;
                            } else if (pagination.page <= 3) {
                                pageNum = i + 1;
                            } else if (pagination.page >= pagination.pages - 2) {
                                pageNum = pagination.pages - 4 + i;
                            } else {
                                pageNum = pagination.page - 2 + i;
                            }
                            
                            return (
                                <button
                                    key={pageNum}
                                    className={`join-item btn btn-sm ${pagination.page === pageNum ? 'btn-active' : ''}`}
                                    onClick={() => handlePageChange(pageNum)}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                        
                        <button 
                            className="join-item btn btn-sm" 
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={pagination.page === pagination.pages}
                        >
                            ›
                        </button>
                        <button 
                            className="join-item btn btn-sm" 
                            onClick={() => handlePageChange(pagination.pages)}
                            disabled={pagination.page === pagination.pages}
                        >
                            »
                        </button>
                    </div>
                </div>
            )}

            {/* Modal para la edición masiva */}
            {showBulkEditModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="text-2xl font-bold mb-4">
                            Edición Masiva de Artículos
                        </h3>
                        
                        {!bulkEditResult ? (
                            <form onSubmit={handleBulkEditSubmit} className="space-y-4">
                                <div className="alert alert-info mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div>
                                        <p>Se actualizarán <span className="font-medium">{selectedArticlesForEdit.length} artículos</span>.</p>
                                        <p>Solo se modificarán los campos que contengan valores. Los campos vacíos se mantendrán sin cambios.</p>
                                    </div>
                                </div>
                                
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">País</span>
                                    </label>
                                    <select
                                        name="pais"
                                        value={bulkEditFormData.pais}
                                        onChange={handleBulkEditInputChange}
                                        className="select select-bordered w-full"
                                    >
                                        <option value="">No cambiar</option>
                                        {countryOptions.map(({ code, name }) => (
                                            <option key={code} value={code}>
                                                {name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Ciudad</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="ciudad"
                                        value={bulkEditFormData.ciudad}
                                        onChange={handleBulkEditInputChange}
                                        placeholder="Ciudad para todos los artículos seleccionados"
                                        className="input input-bordered w-full"
                                    />
                                </div>
                                
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Tipo de Artículo</span>
                                    </label>
                                    <select
                                        name="tipo"
                                        value={bulkEditFormData.tipo}
                                        onChange={handleBulkEditInputChange}
                                        className="select select-bordered w-full"
                                    >
                                        <option value="">Seleccionar tipo...</option>
                                        {articleTypes.map((tipo, index) => (
                                            <option key={index} value={tipo}>
                                                {tipo}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="modal-action">
                                    <button 
                                        type="button"
                                        className="btn btn-ghost" 
                                        onClick={handleCloseBulkEditModal}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={bulkEditLoading || (!bulkEditFormData.pais && !bulkEditFormData.ciudad && !bulkEditFormData.tipo)}
                                    >
                                        {bulkEditLoading ? (
                                            <>
                                                <span className="loading loading-spinner loading-sm"></span>
                                                Actualizando...
                                            </>
                                        ) : (
                                            'Actualizar Artículos'
                                        )}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="space-y-4">
                                <div className="alert alert-success">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>
                                        Se han actualizado {bulkEditResult.updatedCount} artículos correctamente.
                                    </span>
                                </div>
                                
                                <div className="modal-action">
                                    <button 
                                        className="btn btn-primary" 
                                        onClick={handleCloseBulkEditModal}
                                    >
                                        Cerrar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal para eliminación en bulk */}
            {showBulkDeleteModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="text-2xl font-bold mb-4 text-error">
                            Eliminar Artículos
                        </h3>
                        
                        <div className="alert alert-warning mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <div>
                                <p>¿Estás seguro de que quieres eliminar <span className="font-bold">{selectedArticlesForEdit.length} artículos</span>?</p>
                                <p className="text-sm">Esta acción NO se puede deshacer.</p>
                            </div>
                        </div>
                        
                        <div className="modal-action">
                            <button 
                                className="btn btn-ghost" 
                                onClick={handleCloseBulkDeleteModal}
                                disabled={bulkDeleteLoading}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn btn-error"
                                onClick={handleBulkDeleteSubmit}
                                disabled={bulkDeleteLoading}
                            >
                                {bulkDeleteLoading ? (
                                    <>
                                        <span className="loading loading-spinner loading-sm"></span>
                                        Eliminando...
                                    </>
                                ) : (
                                    'Eliminar Artículos'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para artículos huérfanos */}
            {showOrphanedModal && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-4xl">
                        <h3 className="text-2xl font-bold mb-4">
                            Artículos Huérfanos (Sin Ventas)
                        </h3>
                        
                        {orphanedArticles.length === 0 ? (
                            <div className="alert alert-success">
                                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>¡Perfecto! No se encontraron artículos huérfanos.</span>
                            </div>
                        ) : (
                            <>
                                <div className="alert alert-info mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div>
                                        <p>Se encontraron <span className="font-bold">{orphanedArticles.length} artículos</span> que no tienen ventas asociadas.</p>
                                        <p className="text-sm">Revisa la lista antes de eliminar.</p>
                                    </div>
                                </div>
                                
                                <div className="overflow-x-auto max-h-96">
                                    <table className="table table-zebra w-full">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Identificador</th>
                                                <th>Nombre</th>
                                                <th>País</th>
                                                <th>Ciudad</th>
                                                <th>Creado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orphanedArticles.map(article => (
                                                <tr key={article.id}>
                                                    <td>{article.id}</td>
                                                    <td className="font-mono text-sm">{article.identificador}</td>
                                                    <td>{article.nombre}</td>
                                                    <td>{article.pais ? getCountryName(article.pais) : '-'}</td>
                                                    <td>{article.ciudad || '-'}</td>
                                                    <td>{new Date(article.createdAt).toLocaleDateString('es-ES')}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                        
                        <div className="modal-action">
                            <button 
                                className="btn btn-ghost" 
                                onClick={() => setShowOrphanedModal(false)}
                                disabled={orphanedLoading}
                            >
                                Cerrar
                            </button>
                            {orphanedArticles.length > 0 && (
                                <button 
                                    className="btn btn-error"
                                    onClick={handleDeleteOrphanedArticles}
                                    disabled={orphanedLoading}
                                >
                                    {orphanedLoading ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm"></span>
                                            Eliminando...
                                        </>
                                    ) : (
                                        `Eliminar ${orphanedArticles.length} Huérfanos`
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación de merge */}
            {showMergeConfirmModal && mergeConfirmData && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="text-2xl font-bold mb-4">
                            Confirmar Merge de Artículos
                        </h3>
                        
                        <div className="alert alert-warning mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <div>
                                <p>¿Estás seguro de que quieres fusionar estos artículos?</p>
                                <p className="text-sm">Esta acción NO se puede deshacer.</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <h4 className="font-bold mb-2">Artículo Destino:</h4>
                                <div className="bg-base-200 p-3 rounded-lg">
                                    <p className="font-medium">{mergeConfirmData.targetArticle.identificador}</p>
                                    <p className="text-sm opacity-70">{mergeConfirmData.targetArticle.nombre}</p>
                                    <p className="text-sm">Ventas: {mergeConfirmData.targetArticle.totalVentas}</p>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-bold mb-2">Artículos a Fusionar:</h4>
                                <div className="max-h-40 overflow-y-auto space-y-2">
                                    {mergeConfirmData.sourceArticles.map(article => (
                                        <div key={article.id} className="bg-base-200 p-3 rounded-lg">
                                            <p className="font-medium">{article.identificador}</p>
                                            <p className="text-sm opacity-70">{article.nombre}</p>
                                            <p className="text-sm">Ventas: {article.totalVentas}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <div className="modal-action">
                            <button 
                                className="btn btn-ghost" 
                                onClick={() => setShowMergeConfirmModal(false)}
                                disabled={mergeLoading}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn btn-primary"
                                onClick={handleConfirmMerge}
                                disabled={mergeLoading}
                            >
                                {mergeLoading ? (
                                    <>
                                        <span className="loading loading-spinner loading-sm"></span>
                                        Fusionando...
                                    </>
                                ) : (
                                    'Confirmar Fusión'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de edición individual */}
            {showModal && selectedArticle && (
                <div className="modal modal-open">
                    <div className="modal-box w-11/12 max-w-2xl">
                        <h3 className="font-bold text-lg">Editar Artículo: {selectedArticle.identificador}</h3>
                        <form onSubmit={handleSubmit} className="py-4">
                            <div className="grid grid-cols-1 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Nombre</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        name="nombre" 
                                        value={formData.nombre} 
                                        onChange={handleInputChange}
                                        className="input input-bordered w-full" 
                                        placeholder="Nombre del artículo"
                                        required
                                    />
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Ciudad</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            name="ciudad" 
                                            value={formData.ciudad} 
                                            onChange={handleInputChange}
                                            className="input input-bordered w-full" 
                                            placeholder="Ciudad del artículo"
                                        />
                                    </div>
                                    
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">País</span>
                                        </label>
                                        <select
                                            name="pais"
                                            value={formData.pais}
                                            onChange={handleInputChange}
                                            className="select select-bordered w-full"
                                        >
                                            <option value="">Sin país</option>
                                            {countryOptions.map(({ code, name }) => (
                                                <option key={code} value={code}>
                                                    {name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Tipo de Artículo</span>
                                    </label>
                                    <select 
                                        name="tipo" 
                                        value={formData.tipo} 
                                        onChange={handleInputChange}
                                        className="select select-bordered w-full"
                                    >
                                        <option value="">Seleccionar tipo...</option>
                                        {articleTypes.map((tipo, index) => (
                                            <option key={index} value={tipo}>
                                                {tipo}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-control">
                                    <label className="cursor-pointer label">
                                        <span className="label-text">Artículo activo</span>
                                        <input 
                                            type="checkbox" 
                                            name="activo" 
                                            checked={formData.activo}
                                            onChange={handleInputChange}
                                            className="checkbox checkbox-primary" 
                                        />
                                    </label>
                                </div>
                            </div>
                            
                            <div className="modal-action">
                                <button type="button" className="btn" onClick={handleCloseModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArticlesScreen; 
